/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { WorkCanContactSearchExternalProjectsResponseDto } from '../models/work-can-contact-search-external-projects-response-dto';
import { WorkCreateExternalProjectReferenceDto } from '../models/work-create-external-project-reference-dto';
import { WorkProject } from '../models/work-project';
import { WorkTenantProjectLink } from '../models/work-tenant-project-link';

@Injectable({ providedIn: 'root' })
export class WorkSelfserviceExternalProjectReferencesV2Service extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `selfserviceProjectLinksV2ControllerFindAll()` */
  static readonly SelfserviceProjectLinksV2ControllerFindAllPath = '/v2/selfservice/projects/{projectId}/external-reference';

  /**
   * Get all external references for a project (for Contacts).
   *
   * Returns all external project links for the given project
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `selfserviceProjectLinksV2ControllerFindAll()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectLinksV2ControllerFindAll$Response(
    params: {
      projectId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkTenantProjectLink>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkSelfserviceExternalProjectReferencesV2Service.SelfserviceProjectLinksV2ControllerFindAllPath, 'get');
    if (params) {
      rb.path('projectId', params.projectId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkTenantProjectLink>>;
      })
    );
  }

  /**
   * Get all external references for a project (for Contacts).
   *
   * Returns all external project links for the given project
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `selfserviceProjectLinksV2ControllerFindAll$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectLinksV2ControllerFindAll(
    params: {
      projectId: number;
    },
    context?: HttpContext
  ): Observable<Array<WorkTenantProjectLink>> {
    return this.selfserviceProjectLinksV2ControllerFindAll$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkTenantProjectLink>>): Array<WorkTenantProjectLink> => r.body)
    );
  }

  /** Path part for operation `selfserviceProjectLinksV2ControllerCreate()` */
  static readonly SelfserviceProjectLinksV2ControllerCreatePath = '/v2/selfservice/projects/{projectId}/external-reference';

  /**
   * Add external project reference (for Contacts).
   *
   * Allows a Contact to link their external project number (e.g., Axova project number at SFG)
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `selfserviceProjectLinksV2ControllerCreate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  selfserviceProjectLinksV2ControllerCreate$Response(
    params: {
      projectId: number;
      body: WorkCreateExternalProjectReferenceDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkTenantProjectLink>> {
    const rb = new RequestBuilder(this.rootUrl, WorkSelfserviceExternalProjectReferencesV2Service.SelfserviceProjectLinksV2ControllerCreatePath, 'post');
    if (params) {
      rb.path('projectId', params.projectId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkTenantProjectLink>;
      })
    );
  }

  /**
   * Add external project reference (for Contacts).
   *
   * Allows a Contact to link their external project number (e.g., Axova project number at SFG)
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `selfserviceProjectLinksV2ControllerCreate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  selfserviceProjectLinksV2ControllerCreate(
    params: {
      projectId: number;
      body: WorkCreateExternalProjectReferenceDto
    },
    context?: HttpContext
  ): Observable<WorkTenantProjectLink> {
    return this.selfserviceProjectLinksV2ControllerCreate$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkTenantProjectLink>): WorkTenantProjectLink => r.body)
    );
  }

  /** Path part for operation `selfserviceProjectLinksV2ControllerSearchExternalProjects()` */
  static readonly SelfserviceProjectLinksV2ControllerSearchExternalProjectsPath = '/v2/selfservice/projects/{projectId}/external-reference/search';

  /**
   * Search for own external project (for Contacts).
   *
   * Allows a Contact to search for their own project on their external tenant. Tenant is determined automatically based on logged-in Contact.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `selfserviceProjectLinksV2ControllerSearchExternalProjects()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectLinksV2ControllerSearchExternalProjects$Response(
    params: {
      numberQualified: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkProject>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkSelfserviceExternalProjectReferencesV2Service.SelfserviceProjectLinksV2ControllerSearchExternalProjectsPath, 'get');
    if (params) {
      rb.query('numberQualified', params.numberQualified, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkProject>>;
      })
    );
  }

  /**
   * Search for own external project (for Contacts).
   *
   * Allows a Contact to search for their own project on their external tenant. Tenant is determined automatically based on logged-in Contact.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `selfserviceProjectLinksV2ControllerSearchExternalProjects$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectLinksV2ControllerSearchExternalProjects(
    params: {
      numberQualified: string;
    },
    context?: HttpContext
  ): Observable<Array<WorkProject>> {
    return this.selfserviceProjectLinksV2ControllerSearchExternalProjects$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkProject>>): Array<WorkProject> => r.body)
    );
  }

  /** Path part for operation `selfserviceProjectLinksV2ControllerCanSearchExternalProjects()` */
  static readonly SelfserviceProjectLinksV2ControllerCanSearchExternalProjectsPath = '/v2/selfservice/projects/{projectId}/external-reference/can-search';

  /**
   * Check if Contact can search for external projects.
   *
   * Returns whether the logged-in Contact has an active external tenant configured for searching projects.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `selfserviceProjectLinksV2ControllerCanSearchExternalProjects()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectLinksV2ControllerCanSearchExternalProjects$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkCanContactSearchExternalProjectsResponseDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkSelfserviceExternalProjectReferencesV2Service.SelfserviceProjectLinksV2ControllerCanSearchExternalProjectsPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkCanContactSearchExternalProjectsResponseDto>;
      })
    );
  }

  /**
   * Check if Contact can search for external projects.
   *
   * Returns whether the logged-in Contact has an active external tenant configured for searching projects.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `selfserviceProjectLinksV2ControllerCanSearchExternalProjects$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectLinksV2ControllerCanSearchExternalProjects(
    params?: {
    },
    context?: HttpContext
  ): Observable<WorkCanContactSearchExternalProjectsResponseDto> {
    return this.selfserviceProjectLinksV2ControllerCanSearchExternalProjects$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkCanContactSearchExternalProjectsResponseDto>): WorkCanContactSearchExternalProjectsResponseDto => r.body)
    );
  }

  /** Path part for operation `selfserviceProjectLinksV2ControllerRemove()` */
  static readonly SelfserviceProjectLinksV2ControllerRemovePath = '/v2/selfservice/projects/{projectId}/external-reference/{linkId}';

  /**
   * Delete an external project reference (for Contacts).
   *
   * Allows a Contact to remove an incorrectly created external project link
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `selfserviceProjectLinksV2ControllerRemove()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectLinksV2ControllerRemove$Response(
    params: {
      projectId: number;
      linkId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(this.rootUrl, WorkSelfserviceExternalProjectReferencesV2Service.SelfserviceProjectLinksV2ControllerRemovePath, 'delete');
    if (params) {
      rb.path('projectId', params.projectId, {});
      rb.path('linkId', params.linkId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'text', accept: '*/*', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Delete an external project reference (for Contacts).
   *
   * Allows a Contact to remove an incorrectly created external project link
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `selfserviceProjectLinksV2ControllerRemove$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectLinksV2ControllerRemove(
    params: {
      projectId: number;
      linkId: number;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.selfserviceProjectLinksV2ControllerRemove$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

}
